import { Mixpanel, MxEvent } from "../common/Mixpanel";
import { isCameraValid } from "../components/admin/curator/components/camera/useCameraActions";
import { sendUnityEvent } from "../components/container/unityContainer";
import {
  isUserOnboarding,
  setTooltipData,
} from "../components/gamificationTooltip/helpers";
import {
  CURSOR_MODE,
  MEASURE_UNIT_NAME,
} from "../config/constant/unityConstants";
import { CURATOR_MENU_SECTION } from "../redux/slicers/admin/curatorMenuPreferencesSlicer";
import {
  curatorActions,
  selectActiveMeasureUnitsName,
  selectCursorMode,
  selectIs2DMode,
  selectSidebarSection,
  setSelectedResolutionPreset,
} from "../redux/slicers/admin/curatorSlicer";
import {
  selectExpertMode,
  selectIsAnyObjectSelected,
  selectIsStyleableObject,
  selectSelectedObjectName,
  setExpertMode,
} from "../redux/slicers/admin/curatorUnityObjectSlicer";
import {
  selectPopupOpen,
  shortcutsModuleActions,
} from "../redux/slicers/admin/shortcutsModuleSlicer";
import { selectCameraEditData } from "../redux/slicers/camera/cameraEditSlicer";
import {
  setGlobalHeight,
  setGlobalSafeCameraFlag,
  setGlobalWidth,
} from "../redux/slicers/camera/cameraSettings";
import { selectIsCameraEditing } from "../redux/slicers/camera/projectCameraAngleSlicer";
import { getAppState } from "../redux/store";
import { convertMetersToUnits, convertToMeters } from "./jsHelper";
import { dispatcher } from "./projectHelper";
import { errorToast } from "./toastHelper";
import { UnityEvent, waitForEventOnce } from "./unityPubSub";

export const isShortcutsOpen = () => selectPopupOpen(getAppState());

export const isCurrentCameraValid = ({ showToasts = true } = {}) => {
  const isCameraEditing = selectIsCameraEditing(getAppState());
  if (isCameraEditing) {
    const camera = selectCameraEditData(getAppState());
    return isCameraValid(camera, { showToasts });
  }

  return true;
};

export const toggleSidebarSection = async (sectionName) => {
  let eventName = null;
  if (!isCurrentCameraValid()) return false;

  // check if style section can be opened
  if (sectionName === CURATOR_MENU_SECTION.STYLE) {
    const valid = validateStyleable();
    if (!valid) return false;

    if (isUserOnboarding()) {
      setTooltipData({
        step: "ADD_FINISH",
      });
    }

    const result = await Promise.race([
      waitForEventOnce(UnityEvent.OnMessageReceivedFromUnity),
      // TODO: we need to cancel it on error
      waitForEventOnce(UnityEvent.ReceiveSelectedObjectInfo, () => {
        sendUnityEvent("MaterialModule", "OnClickEditMaterial");
      }),
    ]);

    if (result?.isError) {
      return;
    }
  }

  // check if camera is possible to open
  if (
    sectionName === CURATOR_MENU_SECTION.CAMERAS &&
    selectIs2DMode(getAppState())
  ) {
    errorToast("Please switch to 3d mode to use cameras");
    return false;
  }

  if (sectionName === CURATOR_MENU_SECTION.CAMERAS && isUserOnboarding()) {
    setTooltipData({
      step: "ADD_SAVE_CAMERA",
    });
  }

  dispatcher(curatorActions.toggleSidebarSection(sectionName));

  if (sectionName && isShortcutsOpen()) {
    dispatcher(shortcutsModuleActions.setPopupOpen(false));
  }
  
  switch (sectionName) {
    case "products":
      eventName = MxEvent.CURATOR_OPENED_PANEL_PRODUCT;
      break;
    case "style":
      eventName = MxEvent.CURATOR_OPENED_PANEL_STYLE;
      break;
    case "cameras":
      eventName = MxEvent.CURATOR_OPENED_PANEL_CAMERA;
      break;
    case "light":
      eventName = MxEvent.CURATOR_OPENED_PANEL_LIGHT;
      break;
    case "images":
      eventName = MxEvent.CURATOR_OPENED_PANEL_IMAGES;
      break;
    case "settings":
      eventName = MxEvent.CURATOR_OPENED_PANEL_SETTINGS;
      break;
    case "metadata":
      eventName = MxEvent.CURATOR_OPENED_PANEL_METADATA;
      break;
    case "automation":
      eventName = MxEvent.CURATOR_OPENED_PANEL_AUTOMATION;
      break;
    case "exclusiveprop":
      eventName = MxEvent.CURATOR_OPENED_PANEL_EXCLUSIVE;
      break;
    case "more":
      eventName = MxEvent.CURATOR_OPENED_PANEL_MORE;
      break;
    case "Tile": 
      eventName = MxEvent.CURATOR_OPENED_PANEL_TILEGROUT;
      break;
  }
  Mixpanel.track(true, eventName);

  return true;
};

export const openSidebarSection = (sectionName) => {
  const currentSection = selectSidebarSection(getAppState());
  if (currentSection !== sectionName) {
    return toggleSidebarSection(sectionName);
  }
};

export const closeSidebar = () => {
  dispatcher(curatorActions.toggleSidebarSection(null));
};

export const clearUI = () => {
  dispatcher(shortcutsModuleActions.setPopupOpen(false));
  if (isCurrentCameraValid({ showToasts: false })) {
    dispatcher(curatorActions.toggleSidebarSection(null));
  }
};

export const validateStyleable = () => {
  const isStyleable = selectIsStyleableObject(getAppState());
  const anyObjectSelected = selectIsAnyObjectSelected(getAppState());

  if (!anyObjectSelected) {
    errorToast("Please select an object", {
      toastId: "OBJECT_SELECTION_MISSING",
    });
    return false;
  }

  if (!isStyleable) {
    errorToast("This object is not customisable", {
      toastId: "NOT_STYLEABLE_OBJECT",
    });
    return false;
  }

  return true;
};

export const toggleExpertMode = ({ enabled, objectName } = {}, currentUser) => {
  if (!objectName) {
    objectName = selectSelectedObjectName(getAppState());
  }

  if (typeof enabled === "undefined") {
    const expertMode = selectExpertMode(getAppState(), objectName);
    enabled = !expertMode;
  }

  dispatcher(
    setExpertMode({
      objectName,
      enabled,
    })
  );
  Mixpanel.track( true, 
    enabled
      ? MxEvent.CURATOR_STYLE_EXPERT_MODE_ON
      : MxEvent.CURATOR_STYLE_EXPERT_MODE_OFF
  );
};

export const canChangeSelectedAxis = (state, { showWarnings } = {}) => {
  state = state || getAppState();
  const cursorMode = selectCursorMode(state);
  const anyObjectSelected = selectIsAnyObjectSelected(state);

  if (cursorMode === CURSOR_MODE.STYLE_ONLY) {
    showWarnings &&
      errorToast(
        "It is not possible to change selected axis while being in style only mode"
      );
    return false;
  }

  if (!anyObjectSelected) {
    showWarnings && errorToast("Please select an object");
    return false;
  }

  return true;
};

export const selectSettingsResolutionPreset = (
  presetItem,
  enableSafeCameraFrame = false
) => {
  dispatcher(setSelectedResolutionPreset(presetItem.id));
  dispatcher(setGlobalWidth(presetItem.width));
  dispatcher(setGlobalHeight(presetItem.height));

  if (enableSafeCameraFrame) {
    dispatcher(setGlobalSafeCameraFlag(true));
  }
};

export const convertActiveUnitToMeters = (value) => {
  const measureUnitName = selectActiveMeasureUnitsName(getAppState());
  return convertToMeters(value, measureUnitName);
};

export const convertCMToActiveUnits = (value) => {
  const measureUnitName = selectActiveMeasureUnitsName(getAppState());

  if (!measureUnitName || measureUnitName === MEASURE_UNIT_NAME.CM) {
    return value;
  }

  const meters = convertToMeters(value, MEASURE_UNIT_NAME.CM);
  return convertMetersToUnits(meters, measureUnitName);
};

export const convertActiveUnitToCM = (value) => {
  const measureUnitName = selectActiveMeasureUnitsName(getAppState());

  if (!measureUnitName || measureUnitName === MEASURE_UNIT_NAME.CM) {
    return value;
  }

  const meters = convertToMeters(value, measureUnitName);
  return convertMetersToUnits(meters, MEASURE_UNIT_NAME.CM);
};
